import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { SectionLabel, RainbowTop, ArrowLink } from '../components/shared';
import SaveButton from '../components/SaveButton';
import Newsletter from '../components/Newsletter';
import Hero from '../components/Hero';
import AdSlot from '../components/AdSlot';
import AffiliateBlock from '../components/AffiliateBlock';
import { IMAGES, EMIRATES, STATS, HOME_START_CARDS, CULTURE_CARDS } from '../mock';

const Home = () => {
  useReveal([]);

  return (
    <div>
      {/* HERO */}
      <Hero />

      {/* BEFORE YOU BOOK */}
      <section className="bg-[#F6F1E3]">
        <div className="max-w-5xl mx-auto px-5 py-8">
          <div className="reveal flex items-start gap-3 rounded-xl border-l-4 border-[#e0564e] bg-[#F2ECCF] px-5 py-4">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-[#e0564e]" />
            <p className="text-[15px] leading-relaxed text-[#5a4a33]">
              <span className="font-semibold text-[#1B1D21]">Before you book:</span>{' '}
              travel advice and regional conditions can change.{' '}
              <a
                href="https://www.smartraveller.gov.au/destinations/middle-east/united-arab-emirates"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#2F663C] underline underline-offset-2 hover:text-[#1B1D21]"
              >
                Read the current Australian Government (Smartraveller) advice for the UAE
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* EMIRATES */}
      <section className="bg-[#fbefdf]">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel className="reveal">Seven emirates, one country</SectionLabel>
            <h2 className="reveal mt-4 font-display text-[36px] sm:text-[44px] leading-[1.1] font-semibold text-[#1B1D21]">
              Most Australians only see Dubai. Here&apos;s the rest.
            </h2>
            <p className="reveal mt-5 text-[18px] leading-relaxed text-[#4a5f72]">
              Every emirate is under two hours from Dubai. Pick the one that matches your
              trip —{' '}
              <Link to="/emirates" className="text-[#2F663C] font-semibold underline underline-offset-2 hover:text-[#1B1D21]">
                or read the comparison first
              </Link>
              .
            </p>
          </div>

          <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {EMIRATES.map((e) => (
              <Link
                key={e.slug}
                to={`/emirates/${e.slug}`}
                className="reveal lift group relative bg-white rounded-2xl overflow-hidden shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] border border-[#E6D7A2] flex flex-col"
              >
                <RainbowTop />
                <div className="relative h-44 overflow-hidden">
                  <img src={e.image} alt={e.name} className="img-zoom w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a2b]/70 via-transparent to-transparent" />
                  <span className="absolute bottom-3 left-5 text-[12px] font-semibold tracking-[0.16em] uppercase text-white/90">
                    Emirate {e.number}
                  </span>
                  <div className="absolute top-3 right-3 z-10">
                    <SaveButton item={{ type: 'emirate', ref_id: `emirate-${e.slug}`, title: e.name, subtitle: 'Emirate guide', to: `/emirates/${e.slug}` }} />
                  </div>
                </div>
                <div className="p-7 flex flex-col grow">
                  <h3 className="font-display text-[26px] font-semibold text-[#1B1D21]">
                    {e.name}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#4B4F58] grow">
                    {e.tagline}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#2F663C] group-hover:text-[#1B1D21] transition-colors">
                    Read the guide
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#1B1D21]">
        <div className="max-w-7xl mx-auto px-5 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {STATS.map((s) => (
              <div key={s.label} className="reveal text-center">
                <div className="font-display text-[48px] leading-none font-semibold sunset-text">
                  {s.value}
                </div>
                <div className="mt-3 text-[14px] tracking-wide uppercase text-[#9fb2c4]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* START HERE */}
      <section className="bg-[#F6F1E3]">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel className="reveal">Start Here</SectionLabel>
            <h2 className="reveal mt-4 font-display text-[36px] sm:text-[44px] leading-[1.1] font-semibold text-[#1B1D21]">
              Plan your trip or your move
            </h2>
            <p className="reveal mt-5 text-[18px] leading-relaxed text-[#4a5f72]">
              The four questions every visitor and relocating expat asks first.
            </p>
          </div>

          <div className="mt-14 grid gap-7 md:grid-cols-3">
            {HOME_START_CARDS.map((c) => (
              <article
                key={c.title}
                className="reveal lift group bg-white rounded-2xl overflow-hidden shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] border border-[#E6D7A2] flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={c.image} alt={c.alt} className="img-zoom w-full h-full object-cover" />
                  <RainbowTop />
                </div>
                <div className="p-7 flex flex-col grow">
                  <span className="text-[12px] font-semibold tracking-[0.16em] uppercase text-[#e0564e]">
                    {c.label}
                  </span>
                  <h3 className="mt-2 font-display text-[22px] leading-snug font-semibold text-[#1B1D21]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#4B4F58] grow">
                    {c.body}
                  </p>
                  <div className="mt-5">
                    <ArrowLink to={c.to}>{c.cta}</ArrowLink>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CULTURE */}
      <section className="bg-[#fbefdf]">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel className="reveal">Culture &amp; Practical Life</SectionLabel>
            <h2 className="reveal mt-4 font-display text-[36px] sm:text-[44px] leading-[1.1] font-semibold text-[#1B1D21]">
              Respect the place, enjoy it more
            </h2>
            <p className="reveal mt-5 text-[18px] leading-relaxed text-[#4a5f72]">
              Customs, etiquette, dress codes and the small things that make a trip (or a
              move) go smoothly.
            </p>
          </div>

          <div className="mt-14 grid gap-7 md:grid-cols-2">
            {CULTURE_CARDS.map((c) => (
              <article
                key={c.title}
                className="reveal lift group bg-white rounded-2xl overflow-hidden shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] border border-[#E6D7A2]"
              >
                <div className="relative h-60 overflow-hidden">
                  <img src={c.image} alt={c.alt} className="img-zoom w-full h-full object-cover" />
                  <RainbowTop />
                </div>
                <div className="p-7">
                  <span className="text-[12px] font-semibold tracking-[0.16em] uppercase text-[#e0564e]">
                    {c.label}
                  </span>
                  <h3 className="mt-2 font-display text-[24px] leading-snug font-semibold text-[#1B1D21]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#4B4F58]">
                    {c.body}
                  </p>
                  <div className="mt-5">
                    <ArrowLink to={c.to}>{c.cta}</ArrowLink>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TRAVEL ESSENTIALS (affiliate) + AD */}
      <section className="bg-[#F6F1E3]">
        <div className="max-w-7xl mx-auto px-5 pt-16">
          <AffiliateBlock
            placement="home"
            title="Sort the essentials before you fly"
            subtitle="The things every UAE trip needs — booked through partners we trust."
            columns={3}
          />
        </div>
        <div className="max-w-5xl mx-auto px-5 pt-12">
          <AdSlot slot="inContent" minH={140} />
        </div>
      </section>

      {/* NEWSLETTER + MONETIZATION NOTE */}
      <section className="bg-[#F6F1E3]">
        <div className="max-w-4xl mx-auto px-5 py-14 space-y-8">
          <div className="reveal">
            <Newsletter variant="light" />
          </div>
          <div className="reveal rounded-2xl border border-[#E6D7A2] bg-[#EFE7D2] px-7 py-7 text-center">
            <h3 className="font-display text-[22px] font-semibold text-[#1B1D21]">
              How we make money
            </h3>
            <p className="mt-3 text-[16px] leading-relaxed text-[#4B4F58]">
              This site earns a commission when you book hotels or travel insurance through
              some links, at no extra cost to you. We only recommend what we&apos;d book
              ourselves, and every sponsored link is clearly marked. See our{' '}
              <Link to="/about" className="font-semibold text-[#2F663C] underline underline-offset-2 hover:text-[#1B1D21]">
                full disclosure
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
